import { useState } from 'react';
import { Link } from 'react-router-dom';
import LessonCard from './components/LessonCard';
import { allPrecalculusLessons, precalculusUnits } from './data';
import SEOHead from '../../../components/SEOHead';

export default function PrecalculusPage() {
  const [selectedUnit, setSelectedUnit] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const siteUrl = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: 'Precalculus Full Curriculum | NCTM, Common Core, Ohio & NY Standards | Augustine Anyimadu-Ahenkae',
    description: 'Comprehensive Precalculus curriculum aligned with NCTM, Common Core, Ohio Learning Standards, and NY Next Gen Standards. Advanced functions, trigonometry, complex numbers, vectors, matrices, and conic sections.',
    url: `${siteUrl}/grades/precalculus`,
    provider: { '@type': 'Person', name: 'Augustine Anyimadu-Ahenkae', url: siteUrl },
    educationalLevel: 'Precalculus',
    inLanguage: 'en',
    hasCourseInstance: [
      { '@type': 'CourseInstance', name: 'Unit 1: Advanced Functions & Analysis', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Unit 2: Trigonometry', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Unit 3: Complex Numbers & Polar Coordinates', courseMode: 'online' },
      { '@type': 'CourseInstance', name: 'Unit 4: Vectors & Matrices', courseMode: 'online' },
    ],
  };

  const filteredLessons = allPrecalculusLessons.filter(lesson => {
    const matchesUnit = selectedUnit === 'all' || lesson.unit === selectedUnit;
    const matchesSearch = lesson.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         lesson.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesUnit && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <SEOHead
        title="Precalculus Full Curriculum | NCTM & Common Core | Augustine Anyimadu-Ahenkae"
        description="Master Precalculus with comprehensive lessons on advanced functions, trigonometry, complex numbers, vectors, and conic sections. Aligned with NCTM, Common Core, Ohio, and NY standards. AP Calculus readiness included."
        canonical="/grades/precalculus"
        keywords="Precalculus curriculum, advanced functions, trigonometry, complex numbers, vectors matrices, conic sections, AP Calculus prep, NCTM Common Core"
        jsonLd={jsonLd}
      />
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link
            to="/grades/hub"
            className="inline-flex items-center text-white/90 hover:text-white mb-6 transition-colors"
          >
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Grades Hub
          </Link>
          
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-4xl md:text-5xl font-bold mb-4">Precalculus</h1>
              <p className="text-xl text-indigo-100 max-w-3xl leading-relaxed">
                Master advanced functions, trigonometry, complex numbers, and vectors. Aligned with NCTM, Common Core, Ohio, and New York standards for AP Calculus readiness.
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <div className="flex items-center bg-white/20 px-4 py-2 rounded-lg">
                  <i className="ri-book-open-line text-2xl mr-3"></i>
                  <div>
                    <div className="text-2xl font-bold">{allPrecalculusLessons.length}</div>
                    <div className="text-xs text-indigo-100">Comprehensive Lessons</div>
                  </div>
                </div>
                <div className="flex items-center bg-white/20 px-4 py-2 rounded-lg">
                  <i className="ri-question-answer-line text-2xl mr-3"></i>
                  <div>
                    <div className="text-2xl font-bold">300</div>
                    <div className="text-xs text-indigo-100">Practice Questions</div>
                  </div>
                </div>
                <div className="flex items-center bg-white/20 px-4 py-2 rounded-lg">
                  <i className="ri-award-line text-2xl mr-3"></i>
                  <div>
                    <div className="text-2xl font-bold">4</div>
                    <div className="text-xs text-indigo-100">Standards Aligned</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden lg:block ml-8">
              <div className="w-32 h-32 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                <i className="ri-function-line text-7xl"></i>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Units Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {precalculusUnits.map(unit => (
            <div
              key={unit.id}
              className="bg-white rounded-xl p-5 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
            >
              <div className={`w-12 h-12 bg-gradient-to-br ${unit.color} rounded-lg flex items-center justify-center mb-4`}>
                <i className="ri-book-2-line text-2xl text-white"></i>
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{unit.title}</h3>
              <p className="text-sm text-gray-600 mb-3 leading-relaxed">{unit.description}</p>
              <div className="text-xs font-semibold text-indigo-600">
                {unit.lessonCount} Lessons
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
                <input
                  type="text"
                  placeholder="Search lessons by topic or keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors"
                />
              </div>
            </div>

            {/* Unit Filter */}
            <div className="md:w-64">
              <select
                value={selectedUnit}
                onChange={(e) => setSelectedUnit(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-indigo-500 focus:outline-none transition-colors cursor-pointer"
              >
                <option value="all">All Units</option>
                <option value="Advanced Functions">Advanced Functions</option>
                <option value="Trigonometry">Trigonometry</option>
                <option value="Complex Numbers">Complex Numbers</option>
                <option value="Vectors & Linear Algebra">Vectors & Linear Algebra</option>
              </select>
            </div>
          </div>

          {/* Results Count */}
          <div className="mt-4 text-sm text-gray-600">
            Showing <span className="font-semibold text-indigo-600">{filteredLessons.length}</span> of {allPrecalculusLessons.length} lessons
          </div>
        </div>
      </div>

      {/* Lessons */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {filteredLessons.length > 0 ? (
          <div className="space-y-8">
            {filteredLessons.map(lesson => (
              <LessonCard key={lesson.id} lesson={lesson} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-search-line text-5xl text-gray-400"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No lessons found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your search or filter criteria</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedUnit('all');
              }}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-4">Ready for AP Calculus?</h2>
            <p className="text-xl text-indigo-100 mb-8 max-w-2xl mx-auto">
              Master these precalculus concepts to ensure success in AP Calculus AB and BC
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/grades/ap-calculus-readiness"
                className="px-8 py-4 bg-white text-indigo-600 rounded-lg font-bold hover:bg-indigo-50 transition-colors shadow-lg hover:shadow-xl"
              >
                <i className="ri-calculator-line mr-2"></i>
                Take AP Calculus Readiness Quiz
              </Link>
              <Link
                to="/grades/ap-calculus-ab-exam"
                className="px-8 py-4 bg-indigo-700 text-white rounded-lg font-bold hover:bg-indigo-800 transition-colors shadow-lg hover:shadow-xl"
              >
                <i className="ri-file-list-3-line mr-2"></i>
                Practice AP Calculus AB Exam
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}